import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver } from "../../actions/valueActions";

import { formatString, convertBoolDiffValue } from "./valueFunctions";
import { getIconFromDriverName } from "./iconFunctions";
import CostValueDriverPaymentForm from "./CostValueDriverPaymentForm";
import { getAllOutcomes } from "./valueFunctions";

const CostValueDriverPayment = ({ id, valueModel, driver, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    const [editPaymentDriverId, setEditPaymentDriverId] = useState(null);
    const [deletePaymentDriverPopupOpen, setDeletePaymentDriverPopupOpen] = useState(false);
    const [deletePaymentDriverId, setDeletePaymentDriverId] = useState(null);

    const handleResetPayment = () => {
        setEditPaymentDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    const openDeletePaymentDriver = (id) => {
        setDeletePaymentDriverId(id)
        setDeletePaymentDriverPopupOpen(true)
    }

    const editPaymentDriver = (id) => {
        setEditPaymentDriverId(id)
    }

    const onSubmitPayment = async (values, driver, { setSubmitting, setErrors }) => {
        values.id = editPaymentDriverId
        if (outcome) {
            values.outcome = outcome
        }
        values.driver = driver
        values.annual_contract_value = values.annual_contract_value.toString().replace(/,/g, "");
        values.transaction_value = values.transaction_value.toString().replace(/,/g, "");
        values.transaction_frequency = values.transaction_frequency.toString().replace(/,/g, "");

        let success = false

        if (editPaymentDriverId) {
            const result = await dispatch(editDriver('payment', id, values, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditPaymentDriverId(null)
            }
        }
        else {
            const result = await dispatch(addDriver('payment', id, values, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }

    const paymentDriverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        { id: 'dso_decrease', label: 'DSO change', minWidth: 150, action: false },
        // { id: 'revenue_model', label: 'Revenue model', format: (value) => formatString(value), minWidth: 100, action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createPaymentDriverData = (id, outcome, driver_name, dso_decrease, positive_driver, actions) => {
        dso_decrease = `${dso_decrease} days ${positive_driver ? 'decrease': 'increase'}`
        return { id, outcome, driver_name, dso_decrease, positive_driver, actions };
    }
    
    const paymentDriverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editPaymentDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeletePaymentDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }
    
    const paymentDriverRows = (valueModel?.cost_value_driver?.payment_savings?.filter(driver => !outcome || driver.outcome === outcome) ?? []).map((driver) => {
        return (
            createPaymentDriverData(driver.id, driver.outcome, driver.driver_name, driver.dso_decrease, driver.positive_driver, paymentDriverActions(driver.id))
        )
    })
    
    const paymentDriverTable = () => {
        return (
            <React.Fragment>
                <Divider />
                <Table
                    columns={paymentDriverColumns}
                    rows={paymentDriverRows}
                    selectedId={editPaymentDriverId}
                />
            </React.Fragment>
        )
    }

    const paymentDriverFormActions = () => {
        return (
            <React.Fragment>
                {editPaymentDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editPaymentDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleResetPayment}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    return (
        <Box>
            <Divider sx={{ mb: 3 }} />
            <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Driver type:
                </Typography>
                <Typography variant="caption">
                    Faster payment
                </Typography>
            </Stack>
            <CostValueDriverPaymentForm
                currency="€"
                outcome={outcome}
                outcomeList={getAllOutcomes(valueModel?.driver_analysis)}
                editPaymentDriverId={editPaymentDriverId}
                editPaymentDriver={valueModel?.cost_value_driver?.payment_savings?.find(paymentDriver => paymentDriver.id === editPaymentDriverId)}
                formActions={paymentDriverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitPayment(values, driver, { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {paymentDriverTable()}
            <DeletePopup open={deletePaymentDriverPopupOpen} onClose={() => setDeletePaymentDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('payment', id, deletePaymentDriverId))} />
        </Box>
    )
    
}

export default CostValueDriverPayment